import {
  useMutation,
  type UseMutationResult,
  useQueryClient,
} from '@tanstack/react-query';

import { fetchBackend } from '../apiClient';
import { generateMutationKey } from '../utils';

export interface CreateUserFeedbackRequest {
  investigationId: string;
  rating: 'positive' | 'negative';
  comment?: string;
}

export interface CreateUserFeedbackResponse {
  id: string;
  userId: string;
  userName: string;
  organizationId: string;
  investigationId: string;
  rating: 'positive' | 'negative';
  comment?: string;
  source: 'ui';
  createdAt: string;
  updatedAt: string;
}

export interface UpdateUserFeedbackRequest {
  feedbackId: string;
  rating?: 'positive' | 'negative';
  comment?: string;
}

export interface UpdateUserFeedbackResponse extends CreateUserFeedbackResponse {
  updatedAt: string;
}

const createUserFeedback = async (
  data: CreateUserFeedbackRequest,
): Promise<CreateUserFeedbackResponse> => {
  const response = await fetchBackend(`/api/rca/feedback`, {
    method: 'POST',
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    throw new Error(`Failed to create user feedback: ${response.statusText}`);
  }

  return response.json();
};

const updateUserFeedback = async (
  data: UpdateUserFeedbackRequest,
): Promise<UpdateUserFeedbackResponse> => {
  const response = await fetchBackend(`/api/rca/feedback/${data.feedbackId}`, {
    method: 'PUT',
    body: JSON.stringify({
      rating: data.rating,
      comment: data.comment,
    }),
  });

  if (!response.ok) {
    throw new Error(`Failed to update user feedback: ${response.statusText}`);
  }

  return response.json();
};

const deleteUserFeedback = async (feedbackId: string): Promise<void> => {
  const response = await fetchBackend(`/api/rca/feedback/${feedbackId}`, {
    method: 'DELETE',
  });

  if (!response.ok) {
    throw new Error(`Failed to delete user feedback: ${response.statusText}`);
  }
};

export const useCreateUserFeedback = (): UseMutationResult<
  CreateUserFeedbackResponse,
  unknown,
  CreateUserFeedbackRequest
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: generateMutationKey(['rca', 'feedback'], 'create'),
    mutationFn: createUserFeedback,
    onSuccess: data => {
      // Invalidate and refetch RCA queries that might include this feedback
      queryClient.invalidateQueries({
        queryKey: ['rca'],
      });

      // Invalidate investigation queries
      queryClient.invalidateQueries({
        queryKey: ['investigation', data.investigationId],
      });
    },
  });
};

export const useUpdateUserFeedback = (): UseMutationResult<
  UpdateUserFeedbackResponse,
  unknown,
  UpdateUserFeedbackRequest
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: generateMutationKey(['rca', 'feedback'], 'update'),
    mutationFn: updateUserFeedback,
    onSuccess: data => {
      // Invalidate and refetch RCA queries that might include this feedback
      queryClient.invalidateQueries({
        queryKey: ['rca'],
      });

      // Invalidate investigation queries
      queryClient.invalidateQueries({
        queryKey: ['investigation', data.investigationId],
      });
    },
  });
};

export const useDeleteUserFeedback = (): UseMutationResult<
  void,
  unknown,
  string
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: generateMutationKey(['rca', 'feedback'], 'delete'),
    mutationFn: deleteUserFeedback,
    onSuccess: () => {
      // Invalidate and refetch RCA queries
      queryClient.invalidateQueries({
        queryKey: ['rca'],
      });

      // Invalidate investigation queries
      queryClient.invalidateQueries({
        queryKey: ['investigation'],
      });
    },
  });
};
